import React from "react";
import { Link } from "react-router-dom";
import type { GridColDef } from "@mui/x-data-grid";
import type { DateTime } from "luxon";
import useAsyncRequest from "hooks/useAsyncRequest";
import { formatDateAsLocaleDateString } from "utils/formatters/DateFormatters";
import { nameof } from "utils/nameof";
import { LoadablePage } from "shared/LoadablePage";
import type {
  CloudSubscriptionDto,
  CloudSubscriptionLicensingChannelDto,
  SubscriptionGroupDto,
} from "client/api/CloudSubscriptionApi";
import {
  isInfrastructureSubscriptionLicensingChannel,
  isLegacyBraintreeLicensingChannel,
  isSalesforceContractedLicensingChannel,
  isSubscriptionBasedLicensingChannel,
  isTrialLicensingChannel,
  isZuoraSubscriptionLicensingChannel,
} from "client/api/CloudSubscriptionApi";
import type { ServerLicenseDto, ServerLicensingChannelDto } from "client/api/ServerLicenseApi";
import {
  isSalesforceContractedLicensingChannel as isSalesforceContractedServerLicensingChannel,
  isSubscriptionBasedServerLicensingChannel,
  isTemporaryServerLicensingChannel,
  isTrialServerLicensingChannel,
  isZuoraSubscriptionLicensingChannel as isZuoraSubscriptionServerLicensingChannel,
} from "client/api/ServerLicenseApi";
import type { UserGrantDto, SubscriptionGroupUserGrantDto } from "client/api/UserGrantApi";
import type {
  UserRelatedCloudSubscriptionDto,
  UserRelatedServerLicenseDto,
  UserRelatedSubscriptionGroupDto,
  UserDto,
} from "client/api/UsersApi";
import {
  getUserRelatedServerLicenses,
  getUserRelatedCloudSubscriptions,
  getUserRelatedSubscriptionGroups,
} from "client/api/UsersApi";
import { DataTable } from "components/DataTable";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { cloudSubscriptionRoutes } from "areas/cloud-subscriptions/cloud-subscription-routes";
import { licenseRoutes } from "areas/licenses/license-routes";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

export function UserProducts() {
  const { entity: user } = useEntity<UserDto>();
  if (!user) return <></>;

  return <UserProductsForUser userId={user.id}></UserProductsForUser>;
}

function isSubscriptionGroupUserGrantDto(userGrant: UserGrantDto): userGrant is SubscriptionGroupUserGrantDto {
  return nameof<SubscriptionGroupUserGrantDto>("subscriptionGroupName") in userGrant;
}

function describeCloudChannel(channel: CloudSubscriptionLicensingChannelDto): string | null {
  if (isSalesforceContractedLicensingChannel(channel)) return "Salesforce";
  if (isTrialLicensingChannel(channel)) return "Trial";
  if (isZuoraSubscriptionLicensingChannel(channel)) return "Zuora";
  if (isInfrastructureSubscriptionLicensingChannel(channel)) return "Infrastructure";
  if (isSubscriptionBasedLicensingChannel(channel)) return "Subscription";
  if (isLegacyBraintreeLicensingChannel(channel)) return "Braintree";
  if (channel.expiryDate) return "Trial";
  return null;
}

function describeServerChannel(channel: ServerLicensingChannelDto): string | null {
  if (isSalesforceContractedServerLicensingChannel(channel)) return "Salesforce";
  if (isTrialServerLicensingChannel(channel)) return "Trial";
  if (isZuoraSubscriptionServerLicensingChannel(channel)) return "Zuora";
  if (isSubscriptionBasedServerLicensingChannel(channel)) return "Subscription";
  if (isTemporaryServerLicensingChannel(channel)) return "Temporary";
  return null;
}

const onPageChangeNoOp = (page: number) => {
  // do nothing
};

type UserProductsForUserProps = {
  userId: string;
};

function UserProductsForUser(props: UserProductsForUserProps) {
  const { userId } = props;

  return (
    <DetailsPageLayout pageName="User Products">
      <h2>Server Licenses</h2>
      <UserRelatedServerLicensesDataTable userId={userId} />

      <h2>Cloud Subscriptions</h2>
      <UserRelatedCloudSubscriptionsDataTable userId={userId} />

      <h2>Subscription Groups</h2>
      <UserRelatedSubscriptionGroupsDataTable userId={userId} />
    </DetailsPageLayout>
  );
}

function UserRelatedServerLicensesDataTable(props: { userId: string }) {
  const { data, loading, error } = useAsyncRequest(getUserRelatedServerLicenses, props.userId);
  const serverLicenses = data?.directServerLicenses.concat(data.indirectServerLicenses) ?? [];

  const columns: GridColDef<ReturnType<typeof serverLicenseRowMap>>[] = [
    {
      field: nameof<ServerLicenseDto>("serial"),
      headerName: "Serial",
      minWidth: 200,
      flex: 1,
      renderCell: (cellValues) => {
        const to = cellValues.row.subscriptionId
          ? subscriptionRoutes.detail(cellValues.row.subscriptionId).root
          : licenseRoutes.detail(cellValues.row.id).root;
        return (
          <Link to={to} title="View license details">
            {cellValues.row.serial}
          </Link>
        );
      },
    },
    {
      field: nameof<ServerLicenseDto>("licensingChannel"),
      headerName: "Channel",
      flex: 1,
      valueGetter: (_, row) => describeServerChannel(row.licensingChannel),
    },
    {
      field: nameof<ServerLicenseDto>("salesforceContractId"),
      headerName: "Salesforce contract number",
      minWidth: 200,
      flex: 1,
    },
    {
      field: nameof<ServerLicenseDto>("validFromDate"),
      headerName: "Valid from",
      valueFormatter: (value) => formatDateAsLocaleDateString(value, false, false),
    },
    {
      field: nameof<ServerLicenseDto>("expiryDate"),
      headerName: "Expiry date",
      valueFormatter: (value) => formatDateAsLocaleDateString(value, false, false),
    },
    { field: nameof<UserGrantDto>("role"), headerName: "Role", minWidth: 100, flex: 1 },
    {
      field: nameof<SubscriptionGroupUserGrantDto>("subscriptionGroupName"),
      headerName: "Inherited from",
      minWidth: 50,
      flex: 0.5,
    },
  ];

  const serverLicenseRowMap = (row: UserRelatedServerLicenseDto) => {
    const channel = row.serverLicense.licensingChannel;
    return {
      id: row.serverLicense.id,
      subscriptionId: channel.subscriptionId,
      serial: row.serverLicense.serial,
      licensingChannel: channel,
      salesforceContractId: row.serverLicense.salesforceContractId,
      validFromDate: row.serverLicense.validFromDate,
      expiryDate: row.serverLicense.expiryDate,
      subscriptionGroupName: isSubscriptionGroupUserGrantDto(row.userGrant)
        ? row.userGrant.subscriptionGroupName
        : null,
      role: row.userGrant.role.name,
    };
  };

  if (loading || serverLicenses.length > 0)
    return (
      <LoadablePage loading={loading} error={error}>
        <DataTable
          rows={serverLicenses.map(serverLicenseRowMap) ?? []}
          columns={columns}
          loading={loading}
          page={1}
          onPageChange={onPageChangeNoOp}
          hideFooter={true}
        />
      </LoadablePage>
    );

  return <>None</>;
}

function UserRelatedCloudSubscriptionsDataTable(props: { userId: string }) {
  const { data, loading, error } = useAsyncRequest(getUserRelatedCloudSubscriptions, props.userId);
  const cloudSubscriptions = data?.directCloudSubscriptions.concat(data.indirectCloudSubscriptions) ?? [];

  const getCloudSubscriptionExpiryDate = (_: unknown, row: ReturnType<typeof cloudSubscriptionRowMap>) => {
    const licensingChannel = row.licensingChannel;
    if (isSalesforceContractedLicensingChannel(licensingChannel) || isTrialLicensingChannel(licensingChannel)) {
      return licensingChannel.expiryDate;
    }
    return null;
  };

  const columns: GridColDef<ReturnType<typeof cloudSubscriptionRowMap>>[] = [
    {
      field: nameof<CloudSubscriptionDto>("serial"),
      headerName: "Serial",
      minWidth: 200,
      flex: 1,
      renderCell: (cellValues) => {
        const to = cellValues.row.subscriptionId
          ? subscriptionRoutes.detail(cellValues.row.subscriptionId).root
          : cloudSubscriptionRoutes.detail(cellValues.row.id).root;
        return (
          <Link to={to} title="View cloud subscription details">
            {cellValues.row.serial}
          </Link>
        );
      },
    },
    {
      field: nameof<CloudSubscriptionDto>("licensingChannel"),
      headerName: "Channel",
      flex: 1,
      valueGetter: (_, row) => describeCloudChannel(row.licensingChannel),
    },
    {
      field: nameof<CloudSubscriptionDto>("dnsPrefix"),
      headerName: "DNS prefix",
      minWidth: 100,
      flex: 1,
    },
    {
      field: nameof<CloudSubscriptionDto>("cloudSubscriptionStatus"),
      headerName: "Status",
    },
    {
      field: `${nameof<CloudSubscriptionDto>("licensingChannel")}.${nameof<CloudSubscriptionLicensingChannelDto>(
        "expiryDate"
      )}`,
      headerName: "Expiry date",
      valueGetter: getCloudSubscriptionExpiryDate,
      valueFormatter: (value?: DateTime) => (value ? formatDateAsLocaleDateString(value, false, false) : null),
    },
    { field: nameof<UserGrantDto>("role"), headerName: "Role", minWidth: 100, flex: 1 },
    {
      field: nameof<SubscriptionGroupUserGrantDto>("subscriptionGroupName"),
      headerName: "Inherited from",
      minWidth: 50,
      flex: 0.5,
    },
  ];

  const cloudSubscriptionRowMap = (row: UserRelatedCloudSubscriptionDto) => {
    const channel = row.cloudSubscription.licensingChannel;
    return {
      id: row.cloudSubscription.id,
      subscriptionId: channel.subscriptionId,
      serial: row.cloudSubscription.serial,
      dnsPrefix: row.cloudSubscription.dnsPrefix,
      licensingChannel: channel,
      cloudSubscriptionStatus: row.cloudSubscription.cloudSubscriptionStatus,
      subscriptionGroupName: isSubscriptionGroupUserGrantDto(row.userGrant)
        ? row.userGrant.subscriptionGroupName
        : null,
      role: row.userGrant.role.name,
    };
  };

  if (loading || cloudSubscriptions.length > 0)
    return (
      <LoadablePage loading={loading} error={error}>
        <DataTable
          rows={cloudSubscriptions.map(cloudSubscriptionRowMap)}
          columns={columns}
          loading={loading}
          page={1}
          onPageChange={onPageChangeNoOp}
          hideFooter={true}
        />
      </LoadablePage>
    );

  return <>None</>;
}

function UserRelatedSubscriptionGroupsDataTable(props: { userId: string }) {
  const { data, loading, error } = useAsyncRequest(getUserRelatedSubscriptionGroups, props.userId);

  const columns: GridColDef[] = [
    { field: nameof<SubscriptionGroupDto>("id"), headerName: "ID", minWidth: 100, flex: 1 },
    { field: nameof<SubscriptionGroupDto>("name"), headerName: "Name", minWidth: 100, flex: 1 },
    { field: nameof<UserGrantDto>("role"), headerName: "Role", minWidth: 100, flex: 1 },
  ];

  const subscriptionGroupRowMap = (row: UserRelatedSubscriptionGroupDto) => {
    return {
      id: row.userGrant.id,
      name: row.subscriptionGroup.name,
      role: row.userGrant.role.name,
    };
  };

  if (loading || data?.subscriptionGroups?.length)
    return (
      <LoadablePage loading={loading} error={error}>
        <DataTable
          rows={data?.subscriptionGroups?.map(subscriptionGroupRowMap) ?? []}
          columns={columns}
          loading={loading}
          page={1}
          onPageChange={onPageChangeNoOp}
          hideFooter={true}
        />
      </LoadablePage>
    );

  return <>None</>;
}
